import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import s from "./SportsTab.module.css";
import { syLabel } from "../../../../utils/schoolYear";

export default function SportsTab({ studentId, schoolYears = [] }) {
  const [records, setRecords] = useState([]);
  const [sports,  setSports]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");
    Promise.all([
      fetch(`${API_BASE}/api/student-sports?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sports`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([recData, sportData]) => {
        if (cancelled) return;
        setRecords(Array.isArray(recData)  ? recData  : []);
        setSports(Array.isArray(sportData) ? sportData : []);
      })
      .catch(() => { if (!cancelled) setError("Could not load sports records."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const syById    = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const sportById = useMemo(() => Object.fromEntries(sports.map(sp => [sp.id, sp])),   [sports]);

  /* Group by school year, newest first */
  const grouped = useMemo(() => {
    const map = new Map();
    [...records]
      .sort((a, b) => (b.schoolYearId || 0) - (a.schoolYearId || 0))
      .forEach(r => {
        const key = r.schoolYearId ?? "unknown";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
      });
    return [...map.entries()];
  }, [records]);

  if (loading) return <TabSpinner label="Loading sports records…" />;
  if (error)   return <TabError message={error} />;
  if (records.length === 0) return (
    <TabEmpty
      icon="🏆"
      title="No sports participation"
      sub="Sports assignments made in the Sports tab will appear here."
    />
  );

  return (
    <div className={s.root}>
      <div className={s.countLine}>
        {records.length} sports record{records.length !== 1 ? "s" : ""}
      </div>

      {grouped.map(([syId, recs], gIdx) => {
        const sy = syById[syId];
        return (
          <div key={syId} className={s.yearBlock} style={{ animationDelay: `${gIdx * 60}ms` }}>
            <div className={s.yearHead}>
              <span className={s.yearDot} aria-hidden="true" />
              <span className={s.yearLabel}>{sy ? syLabel(sy.label) : "Unknown Year"}</span>
            </div>
            <div className={s.cards}>
              {recs.map(r => {
                const sport = sportById[r.sportId];
                return (
                  <div key={r.id} className={s.card}>
                    <div className={s.sportIcon} aria-hidden="true">🏅</div>
                    <div className={s.cardBody}>
                      <div className={s.sportName}>{sport?.name || `Sport #${r.sportId}`}</div>
                      <div className={s.sportMeta}>
                        {r.position   && <span>Position: {r.position}</span>}
                        {r.jerseyNumber && <span>Jersey #{r.jerseyNumber}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
